import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, AlertTriangle, Check, Loader2, RotateCcw } from "lucide-react";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";

const fmt = (n) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n);

export default function OrderEditItems({ order, onClose, onSaved }) {
  const [quantities, setQuantities] = useState(() => {
    const init = {};
    (order.items || []).forEach((it) => { init[it.id] = it.quantity; });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const items = order.items || [];

  const increment = (id) => setQuantities((q) => ({ ...q, [id]: (q[id] ?? 1) + 1 }));
  const decrement = (id) => setQuantities((q) => ({ ...q, [id]: Math.max(0, (q[id] ?? 1) - 1) }));
  const restore  = (id)  => setQuantities((q) => ({ ...q, [id]: items.find((it) => it.id === id)?.quantity ?? 1 }));

  const hasChanges   = items.some((it) => quantities[it.id] !== it.quantity);
  const removedCount = items.filter((it) => quantities[it.id] === 0).length;
  const changedCount = items.filter((it) => quantities[it.id] !== it.quantity && quantities[it.id] > 0).length;

  const newSubtotal = items.reduce((acc, it) => acc + Number(it.price) * (quantities[it.id] ?? it.quantity), 0);
  const oldSubtotal = items.reduce((acc, it) => acc + Number(it.price) * it.quantity, 0);
  const diff        = newSubtotal - oldSubtotal;

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      const edits = items
        .filter((it) => quantities[it.id] !== it.quantity)
        .map((it) => ({ id: it.id, quantity: quantities[it.id] }));
      const res = await API.patch(`/orders/${order.id}/items`, { items: edits });
      const updated = res.data?.data?.order ?? res.data?.order;
      toast.success("Pedido actualizado. Se notificó al cliente en el chat.");
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error al actualizar el pedido");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
        style={{ background: "rgba(7,10,16,0.75)", backdropFilter: "blur(12px)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ y: 56, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 56, opacity: 0 }}
          transition={{ type: "spring", damping: 30, stiffness: 340 }}
          className="w-full max-w-lg flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden"
          style={{
            maxHeight: "92vh",
            background: "#FEFCF8",
            boxShadow: "0 -8px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.1)",
          }}
        >
          {/* Dark header */}
          <div
            className="shrink-0 px-5 pt-5 pb-4"
            style={{ background: "#111827", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            {/* Drag handle (mobile) */}
            <div className="w-9 h-1 rounded-full mx-auto mb-4 sm:hidden" style={{ background: "rgba(255,255,255,0.15)" }} />

            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div
                    className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase"
                    style={{
                      background: "rgba(254,196,0,0.12)",
                      color: "#fec400",
                      letterSpacing: "0.15em",
                      border: "1px solid rgba(254,196,0,0.2)",
                    }}
                  >
                    Editar pedido
                  </div>
                </div>
                <h2
                  className="text-white font-black text-2xl leading-none"
                  style={{ fontFamily: "'Courier New', monospace", letterSpacing: "-1px" }}
                >
                  #{order.orderNumber}
                </h2>
                <p className="text-sm mt-1.5" style={{ color: "#4b5563" }}>
                  {items.length} producto{items.length !== 1 ? "s" : ""}
                  {hasChanges && (
                    <motion.span
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      style={{ color: "#fec400" }}
                      className="ml-2 font-semibold"
                    >
                      · {removedCount + changedCount} cambio{removedCount + changedCount !== 1 ? "s" : ""}
                    </motion.span>
                  )}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
                style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <X size={16} style={{ color: "#6b7280" }} />
              </button>
            </div>

            {/* Notification warning */}
            <AnimatePresence>
              {hasChanges && (
                <motion.div
                  initial={{ opacity: 0, y: -6, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -6, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-3 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 overflow-hidden"
                  style={{
                    background: "rgba(254,196,0,0.08)",
                    border: "1px solid rgba(254,196,0,0.2)",
                  }}
                >
                  <AlertTriangle size={13} style={{ color: "#fec400", flexShrink: 0 }} />
                  <p className="text-xs leading-relaxed" style={{ color: "rgba(254,196,0,0.85)" }}>
                    El cliente recibirá notificación por chat y el total se recalculará.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((item) => {
              const qty     = quantities[item.id] ?? item.quantity;
              const removed = qty === 0;
              const changed = qty !== item.quantity && !removed;

              return (
                <motion.div
                  key={item.id}
                  layout
                  className="flex items-center gap-3 rounded-2xl overflow-hidden"
                  style={{
                    padding: "11px 13px",
                    background: removed ? "#fff5f5" : changed ? "#fffbeb" : "#ffffff",
                    border: `1px solid ${removed ? "#fca5a5" : changed ? "#fcd34d" : "#ede8df"}`,
                    borderLeft: `4px solid ${removed ? "#ef4444" : changed ? "#fec400" : "#e5e0d5"}`,
                    opacity: removed ? 0.7 : 1,
                    transition: "all 0.22s ease",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Image */}
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-12 h-12 rounded-xl object-cover shrink-0"
                      style={{ filter: removed ? "grayscale(80%) opacity(0.5)" : "none", transition: "filter 0.22s" }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl shrink-0" style={{ background: "#f3f0e8" }} />
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-bold truncate"
                      style={{
                        color: removed ? "#9ca3af" : "#111827",
                        textDecoration: removed ? "line-through" : "none",
                      }}
                    >
                      {item.title}
                    </p>
                    <p className="text-xs tabular-nums mt-0.5" style={{ color: "#6b7280" }}>
                      {fmt(Number(item.price))}
                      {!removed && changed && (
                        <span className="ml-1.5 font-semibold" style={{ color: "#b45309" }}>
                          era ×{item.quantity}
                        </span>
                      )}
                    </p>
                    {item.note && (
                      <p className="text-[10px] italic truncate mt-0.5" style={{ color: "#9ca3af" }}>
                        "{item.note}"
                      </p>
                    )}
                  </div>

                  {/* Controls */}
                  {removed ? (
                    <button
                      onClick={() => restore(item.id)}
                      className="flex items-center gap-1.5 text-xs font-bold shrink-0 rounded-xl px-3 py-2 transition-all active:scale-95"
                      style={{
                        color: "#ef4444",
                        background: "#fff5f5",
                        border: "1px solid #fca5a5",
                      }}
                    >
                      <RotateCcw size={11} />
                      Restaurar
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => decrement(item.id)}
                        className="w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-90"
                        style={{
                          background: qty === 1 ? "#fff5f5" : "#f8f4ec",
                          border: `1px solid ${qty === 1 ? "#fca5a5" : "#e5e0d5"}`,
                        }}
                      >
                        {qty === 1 ? (
                          <Trash2 size={13} style={{ color: "#ef4444" }} />
                        ) : (
                          <Minus size={13} style={{ color: "#374151" }} />
                        )}
                      </button>
                      <span
                        className="w-7 text-center text-sm font-black tabular-nums"
                        style={{ color: changed ? "#92400e" : "#111827" }}
                      >
                        {qty}
                      </span>
                      <button
                        onClick={() => increment(item.id)}
                        className="w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-90"
                        style={{ background: "#f8f4ec", border: "1px solid #e5e0d5" }}
                      >
                        <Plus size={13} style={{ color: "#374151" }} />
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            className="shrink-0 px-4 pb-5 pt-3"
            style={{ borderTop: "1px solid #ede8df", background: "#FEFCF8" }}
          >
            {/* Price summary pill */}
            <AnimatePresence>
              {hasChanges && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ type: "spring", damping: 22, stiffness: 300 }}
                  className="mb-3 flex items-center justify-between rounded-2xl px-4 py-3"
                  style={{
                    background: "#111827",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  }}
                >
                  <div>
                    <p className="text-xs font-semibold" style={{ color: "#6b7280" }}>
                      {removedCount > 0 && `${removedCount} eliminado${removedCount !== 1 ? "s" : ""}`}
                      {removedCount > 0 && changedCount > 0 && "  ·  "}
                      {changedCount > 0 && `${changedCount} cambiado${changedCount !== 1 ? "s" : ""}`}
                    </p>
                    <p className="text-[10px] mt-0.5 tabular-nums" style={{ color: "#374151" }}>
                      Antes: {fmt(oldSubtotal)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: "#6b7280" }}>Nuevo subtotal</p>
                    <p
                      className="text-lg font-black tabular-nums"
                      style={{
                        color: diff < 0 ? "#4ade80" : diff > 0 ? "#f87171" : "#e5e7eb",
                        fontFamily: "'Courier New', monospace",
                      }}
                    >
                      {fmt(newSubtotal)}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action buttons */}
            <div className="flex gap-2.5">
              <button
                onClick={onClose}
                className="px-5 py-3.5 rounded-2xl text-sm font-bold transition-all active:scale-95"
                style={{ border: "1.5px solid #e5e0d5", color: "#374151", background: "transparent" }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex-1 py-3.5 rounded-2xl text-sm font-black flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                style={{
                  background: hasChanges
                    ? "linear-gradient(135deg, #fec400 0%, #fff246 60%, #fec400 100%)"
                    : "#f3f0e8",
                  color: hasChanges ? "#111827" : "#9ca3af",
                  boxShadow: hasChanges
                    ? "0 6px 24px rgba(254,196,0,0.35), 0 2px 8px rgba(254,196,0,0.2)"
                    : "none",
                  transform: hasChanges ? "scale(1)" : "scale(0.98)",
                  transition: "all 0.2s ease",
                  border: "none",
                }}
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {saving ? "Guardando…" : "Confirmar cambios"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
